import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Input, InputNumber, message, Row, Select, Space, Tabs, Typography } from "antd";
import { get, post } from "../../utils/http";

const { Title, Text } = Typography;
const platforms = [
  { key: "ios", label: "iOS" },
  { key: "android", label: "Android" },
];

export default function AppUpdatePage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get("/api/manage/app-update-config");
      if (response.data.code === 0) form.setFieldsValue(response.data.data);
    } catch (error) {
      message.error("获取 App 更新配置失败");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const response = await post("/api/manage/app-update-config", values);
      if (response.data.code === 0) message.success("App 更新配置已保存");
    } catch (error: any) {
      if (!error?.errorFields) message.error("保存 App 更新配置失败");
    } finally {
      setLoading(false);
    }
  };

  const platformForm = (platform: string) => (
    <Row gutter={16}>
      <Col span={8}>
        <Form.Item label="最新版本号" name={["platforms", platform, "latest_version_code"]} rules={[{ required: true }]}>
          <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="例如 1008" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="最低支持版本号" name={["platforms", platform, "minimum_version_code"]} rules={[{ required: true }]}>
          <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="低于此版本将强制更新" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="最新版本名称" name={["platforms", platform, "latest_version_name"]} rules={[{ required: true }]}>
          <Input placeholder="例如 1.1.0" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="更新方式" name={["platforms", platform, "update_type"]} rules={[{ required: true }]}>
          <Select options={[{ label: "整包更新", value: "package" }, { label: "WGT 热更新", value: "wgt" }]} />
        </Form.Item>
      </Col>
      <Col span={16}>
        <Form.Item label="整包/App Store 地址" name={["platforms", platform, "package_url"]}>
          <Input placeholder="整包更新时使用；iOS 填 App Store 地址" />
        </Form.Item>
      </Col>
      <Col span={16}>
        <Form.Item label="WGT 地址" name={["platforms", platform, "wgt_url"]}>
          <Input placeholder="WGT 热更新时使用的 HTTPS 地址" />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="WGT SHA-256" name={["platforms", platform, "wgt_sha256"]}>
          <Input placeholder="64 位 SHA-256" />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100%" }}>
      <Card
        loading={loading}
        title={<Title level={3} style={{ margin: 0 }}>App 更新配置</Title>}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message="最低支持版本会触发强制更新"
          description="保存前请确保对应平台的整包或 WGT 地址已经可访问。WGT 必须与客户端运行时兼容，并建议始终填写 SHA-256。"
        />
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="更新弹窗标题" name="title" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="更新说明" name="message" rules={[{ required: true }]}><Input.TextArea rows={2} /></Form.Item>
            </Col>
          </Row>
          <Tabs items={platforms.map(item => ({ key: item.key, label: item.label, children: platformForm(item.key) }))} />
          <Space>
            <Button type="primary" onClick={saveConfig} loading={loading}>保存配置</Button>
            <Button onClick={fetchConfig} disabled={loading}>重新加载</Button>
            <Text type="secondary">版本号对应 manifest.json 的 versionCode。</Text>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
